import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, Package, Flask, WarningCircle, Printer,
  CheckCircle, Sparkle, FileText, ShoppingCart, PencilSimple,
  ArrowCounterClockwise, Check, Trash, X, ArrowUUpLeft,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { EmptyState } from '@/components/enterprise/EmptyState';
import TraceabilityPanel from '@/pages/trace/TraceabilityPanel';
import { useAuth } from '@/auth/AuthContext';
import { StageStrip } from './StageStrip';
import { NextStepCard } from './NextStepCard';
import { DocChecklist, FolderHint } from './DocChecklist';
import { AiWorkspace } from './AiWorkspace';
import { PartsPlanning } from './PartsPlanning';
import { PrototypeSection } from './PrototypeSection';
import { PhotoGallery } from './PhotoGallery';
import { CompletionCard } from './CompletionCard';
import { HandoverMemoCard } from './HandoverMemoCard';
import { CostCard } from './CostCard';
import { LearningCard } from './LearningCard';
import { ConceptEditForm, ConceptHistory } from './ConceptEdit';
import { DevBasicsEdit } from './DevBasicsEdit';
import { DevDeleteDialog } from './DevDeleteDialog';
import { DevRollbackDialog } from './DevRollbackDialog';
import { GuideHelpButton } from './GuideHelpButton';
import { CONCEPT_FIELDS } from './conceptFields';
import { devApi, STAGE_TONE } from './devApi';
import { jstDateTime } from '@/utils/jstDate';

const CONCEPT_ROWS = CONCEPT_FIELDS.filter((x) => x.key !== 'title');

/**
 * 案件名のその場編集。構想提案の title と同じ1つの値を、同じ PATCH /{did} で
 * 更新する（別の書き込み経路を作らない）。変更履歴にも同じ形で残る。
 */
const InlineTitle = ({ devId, dev, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(dev.title || '');
  const [busy, setBusy] = useState(false);

  const start = () => { setV((dev.concept || {}).title || dev.title || ''); setEditing(true); };

  const save = async () => {
    const next = v.trim();
    if (!next || next === ((dev.concept || {}).title || '')) { setEditing(false); return; }
    setBusy(true);
    try {
      const concept = Object.fromEntries(
        CONCEPT_FIELDS.map((f) => [f.key, (dev.concept || {})[f.key] || '']));
      concept.title = next;
      await devApi.patch(devId, { concept, concept_note: '案件名を変更' });
      toast.success('案件名を変更しました');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || '案件名を変更できませんでした');
    }
    setBusy(false);
  };

  if (!canEdit) return <span data-testid="dev-detail-title">{dev.title}</span>;
  if (!editing) {
    return (
      <button type="button" onClick={start} data-testid="dev-detail-title-edit"
        title="クリックして案件名を変更"
        className="group inline-flex items-center gap-2 text-left hover:text-teal-800">
        <span data-testid="dev-detail-title">{dev.title}</span>
        <PencilSimple size={16}
          className="shrink-0 text-zinc-400 transition-colors group-hover:text-teal-700" />
      </button>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Input value={v} autoFocus maxLength={200} disabled={busy}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-9 w-full max-w-lg text-lg font-bold" data-testid="dev-detail-title-input" />
      <Button size="sm" onClick={save} disabled={busy || !v.trim()}
        data-testid="dev-detail-title-save">
        <Check size={14} className="mr-1" /> {busy ? '保存中…' : '保存'}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setEditing(false)}
        data-testid="dev-detail-title-cancel"><X size={14} /></Button>
    </span>
  );
};

const NotebookHeading = ({ no, title, hint }) => (
  <div className="pt-2" data-testid={`dev-notebook-${no}`}>
    <p className="flex items-baseline gap-2">
      <span className="text-xs font-bold tabular-nums text-teal-700">{no}</span>
      <span className="text-base font-bold text-zinc-900">{title}</span>
    </p>
    {hint && <p className="text-xs text-zinc-600">{hint}</p>}
  </div>
);

const scrollTo = (id) => {
  // Radix のメニューは閉じるときにフォーカスをトリガーへ戻すため、
  // 1フレーム待ってからスクロールする（待たないとスクロールが打ち消される）。
  setTimeout(() => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 120);
};

export default function DevDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const ctx = useOutletContext() || {};
  const { user } = useAuth();
  const isAdmin = !!ctx.access?.is_admin;
  const canEdit = isAdmin || !!ctx.access?.is_member;
  const [data, setData] = useState(null);
  const [plannedParts, setPlannedParts] = useState([]);
  const [meta, setMeta] = useState({ external_resource_types: [] });
  const [gateOpen, setGateOpen] = useState(null);
  const [conceptEdit, setConceptEdit] = useState(false);
  const [basicsEdit, setBasicsEdit] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [forceTo, setForceTo] = useState('');
  const [reason, setReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);

  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    devApi.detail(id)
      .then((r) => { setLoadError(false); setData(r); })
      .catch(() => { setLoadError(true); toast.error('開発案件を読み込めませんでした'); });
  }, [id]);
  useEffect(() => { load(); devApi.meta().then(setMeta).catch(() => {}); }, [load]);

  if (loadError || (data && !data.development)) {
    return (
      <div className="p-6" data-testid="dev-detail-notfound">
        <EmptyState icon={WarningCircle} title="開発案件が見つかりません"
          description="削除された、またはURLが正しくない可能性があります。"
          testid="dev-detail-notfound-empty"
          action={<Button onClick={() => nav('/engineering/developments')}
            data-testid="dev-detail-notfound-back">開発案件の一覧へ</Button>} />
      </div>
    );
  }
  if (!data) return <div className="p-6 text-sm text-zinc-600" data-testid="dev-detail-loading">読み込み中…</div>;

  const d = data.development;
  const next = data.next_stages || [];
  const primaryNext = next.find((n) => !n.blocked) || next[0];

  const advance = async (stage) => {
    try {
      await devApi.advance(id, { to_stage: stage });
      toast.success('ステージを進めました');
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setGateOpen(detail);
        toast.error(`${detail.message || '進めません'} ${(detail.unmet || [])
          .map((u) => u.label).join('、')}`);
      } else toast.error(detail || '進行できませんでした');
    }
  };

  const force = async () => {
    try {
      await devApi.forceStage(id, { to_stage: forceTo, reason });
      toast.success('例外として進めました（履歴に記録されました）');
      setForceOpen(false); setReason(''); setForceTo('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || '操作に失敗しました'); }
  };

  const isDeleted = !!d.deleted_at;
  // 削除済みの案件は書き込みをすべて止める（バックエンドは既定で 404 を返す）。
  const editable = canEdit && !isDeleted;
  const canManageDeletion = isAdmin || (d.owner_user_ids || []).includes(user?.id);

  const restore = async () => {
    try {
      await devApi.restore(id);
      toast.success('開発案件を復元しました');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || '復元できませんでした'); }
  };

  return (
    <div className="space-y-6" data-testid="dev-detail-page">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <InlineTitle devId={id} dev={d} canEdit={canEdit && !isDeleted} onSaved={load} />
            <GuideHelpButton testid="dev-detail-help-btn" />
          </span>
        }
        description={`${d.development_type_label} · ${d.project_name || 'プロジェクト未設定'}${
          d.target_completion_date ? ` · 目標完了日 ${d.target_completion_date}` : ''}`}
        testid="dev-detail-header"
        secondary={
          <>
            <Button variant="outline" onClick={() => nav('/engineering/developments')}
              data-testid="dev-detail-back-btn">
              <ArrowLeft size={15} className="mr-1" /> 一覧
            </Button>
            <StatusBadge tone={STAGE_TONE[d.current_stage] || 'neutral'} testid="dev-detail-stage-badge">
              {d.current_stage_label}
            </StatusBadge>
          </>
        }
        menuItems={[
          ...(canEdit ? [
            { label: '部品を計画する', icon: <Package size={15} />, testid: 'dev-detail-plan-parts-item',
              onClick: () => scrollTo('dev-parts') },
            { label: '部品依頼を作成する', icon: <ShoppingCart size={15} />,
              testid: 'dev-detail-create-request-item', onClick: () => scrollTo('dev-parts') },
            { label: '書類を更新する', icon: <FileText size={15} />,
              testid: 'dev-detail-update-docs-item', onClick: () => scrollTo('dev-documents') },
            { label: 'AIアシスタントを開く', icon: <Sparkle size={15} />,
              testid: 'dev-detail-ai-item', onClick: () => scrollTo('dev-ai') },
            { label: '試作と検証を開く', icon: <Flask size={15} />,
              testid: 'dev-detail-prototypes-item', onClick: () => scrollTo('dev-prototypes') },
            { label: '開発案件を出力する', icon: <Printer size={15} />,
              testid: 'dev-detail-print-item',
              onClick: () => nav(`/engineering/developments/${id}/print`) },
          ] : []),
          ...(isAdmin ? [{
            label: '例外として進む（管理者）',
            icon: <WarningCircle size={15} />,
            testid: 'dev-detail-force-stage-item',
            onClick: () => setForceOpen(true),
          }] : []),
          ...(isAdmin && d.current_stage === 'completed' && !isDeleted ? [{
            label: '完了を取り消す（管理者）',
            icon: <ArrowUUpLeft size={15} />,
            testid: 'dev-detail-rollback-item',
            onClick: () => setRollbackOpen(true),
          }] : []),
          ...(canManageDeletion && !isDeleted ? [{
            label: 'この開発案件を削除する',
            icon: <Trash size={15} />,
            destructive: true,
            testid: 'dev-detail-delete-item',
            onClick: () => setDeleteOpen(true),
          }] : []),
        ]}
        primary={
          <Button onClick={() => scrollTo('dev-next-step')} data-testid="dev-detail-readiness-btn">
            <CheckCircle size={15} className="mr-1" /> 次にやることを見る
          </Button>
        }
      />

      {isDeleted && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3"
          data-testid="dev-detail-deleted-banner">
          <p className="text-sm font-bold text-rose-900">この開発案件は削除されています</p>
          <p className="text-sm text-rose-900">
            {jstDateTime(d.deleted_at)}
            {d.deleted_by_name ? ` · ${d.deleted_by_name}` : ''}
            {d.deleted_reason ? ` · 理由: ${d.deleted_reason}` : ''}
          </p>
          <p className="mt-1 text-xs text-rose-800">
            一覧・ダッシュボード・検索には出ません。記録・履歴・この案件から登録した
            技術資産の由来はそのまま残っています。編集や進行はできません。
          </p>
          {canManageDeletion && (
            <Button size="sm" variant="outline" className="mt-2" onClick={restore}
              data-testid="dev-detail-restore-btn">
              <ArrowCounterClockwise size={14} className="mr-1" /> 削除を取り消す
            </Button>
          )}
        </div>
      )}

      <div id="dev-next-step">
        <NextStepCard g={data.guidance} stageLabel={d.current_stage_label}
          nextStage={primaryNext} canEdit={canEdit && !isDeleted} onAdvance={advance}
          onJump={scrollTo} />
      </div>

      <div className="rounded-lg border border-zinc-300 bg-white p-4 shadow-sm">
        <StageStrip stages={data.stages} current={d.current_stage} iterations={d.stage_iterations} />
        {next.length > 1 && editable && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-200 pt-3">
            <span className="text-xs font-semibold text-zinc-600">他の進め方:</span>
            {next.filter((n) => n.stage !== primaryNext?.stage).map((n) => (
              <Button key={n.stage} variant="outline" size="sm" onClick={() => advance(n.stage)}
                data-testid={`dev-detail-advance-${n.stage}`}>{n.label}へ</Button>
            ))}
          </div>
        )}
        {next.length === 0 && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <StatusBadge tone="success" testid="dev-detail-finished-badge">完了</StatusBadge>
          </div>
        )}
      </div>

      {data.concept_missing.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="dev-concept-missing">
          <p className="text-sm font-semibold text-amber-900">構想提案に未入力があります</p>
          <p className="text-sm text-amber-900">未入力: {data.concept_missing.join('、')}</p>
        </div>
      )}

      <NotebookHeading no="1" title="構想提案" hint="なぜ・何をつくるかの出発点です。" />
      <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-concept-summary">
        <div className="flex items-center justify-between border-b border-zinc-300 bg-zinc-100 px-4 py-2">
          <p className="text-sm font-bold text-zinc-800">構想提案</p>
          {editable && !conceptEdit && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setBasicsEdit((v) => !v)}
                data-testid="dev-basics-edit-btn">
                <PencilSimple size={14} className="mr-1" /> 案件の基本情報を編集
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConceptEdit(true)}
                data-testid="dev-concept-edit-btn">
                <PencilSimple size={14} className="mr-1" /> 構想提案を編集
              </Button>
            </div>
          )}
        </div>
        {basicsEdit && !conceptEdit && (
          <DevBasicsEdit dev={d} onCancel={() => setBasicsEdit(false)}
            onSaved={() => { setBasicsEdit(false); load(); }} />
        )}
        {conceptEdit ? (
          <ConceptEditForm devId={id} concept={d.concept}
            onCancel={() => setConceptEdit(false)}
            onSaved={() => { setConceptEdit(false); load(); }} />
        ) : (
          <dl className="grid gap-x-6 gap-y-3 p-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold text-zinc-600">担当者</dt>
              <dd className="text-sm text-zinc-900" data-testid="dev-lead-name">
                {d.lead_name || (d.owner_names || []).join('、') || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-zinc-600">メンバー</dt>
              <dd className="text-sm text-zinc-900" data-testid="dev-member-names">
                {(d.member_names || []).join('、') || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-zinc-600">受入担当（製造側）</dt>
              <dd className="text-sm text-zinc-900" data-testid="dev-acceptance-name">
                {d.acceptance_name || '—'}
              </dd>
            </div>
            {d.development_type === 'mfg_equipment' && (
              <div>
                <dt className="text-xs font-semibold text-zinc-600">導入先・想定利用者</dt>
                <dd className="text-sm text-zinc-900">
                  {d.intended_users || '—'}{d.install_location ? ` / ${d.install_location}` : ''}
                </dd>
              </div>
            )}
            {CONCEPT_ROWS.map((x) => (
              <div key={x.key} className={x.wide ? 'sm:col-span-2' : ''}>
                <dt className="text-xs font-semibold text-zinc-600">{x.label}</dt>
                <dd className="whitespace-pre-wrap text-sm text-zinc-900" data-testid={`dev-concept-${x.key}`}>
                  {(d.concept || {})[x.key] || '—'}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <ConceptHistory history={data.concept_history} />
      </section>

      <NotebookHeading no="2" title="設計資料" hint="図面・提案書などの根拠となる書類です。" />
      <section className="space-y-2" id="dev-documents">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-zinc-900">設計資料</h2>
          <FolderHint />
        </div>
        <DocChecklist devId={id} checklist={data.checklist} rows={data.design_docs || []}
          kinds={data.design_doc_kinds || []} nasRoots={data.nas_roots || []}
          nasMap={data.nas_map || []}
          nasConfigError={data.nas_config_error || ''}
          canEdit={editable} onChanged={load} />
      </section>

      <div id="dev-readiness" />
      <NotebookHeading no="3" title="必要部品と手配" hint="何が必要で、いま何が足りないかです。" />
      <div id="dev-parts">
        <PartsPlanning devId={id} canEdit={editable} onChanged={load} onParts={setPlannedParts}
          linkedDocs={[
            ...data.checklist.flatMap((c) => c.documents || []),
            ...(data.unassigned_documents || []),
          ]} />
      </div>

      <NotebookHeading no="4" title="試作・検証・改訂"
        hint="つくって確かめた記録です。ここが次の設計の材料になります。" />
      <div id="dev-prototypes">
        <PrototypeSection devId={id} canEdit={editable} plannedParts={plannedParts}
          guidance={data.guidance} onChanged={load} />
        <div className="mt-3"><PhotoGallery devId={id} /></div>
      </div>

      <NotebookHeading no="5" title="完了と登録" hint="かかった費用と、完了後の登録先です。" />
      <div id="dev-completion">
        <CostCard devId={id} />
      </div>

      <div id="dev-completion-card">
        <HandoverMemoCard devId={id} memo={d.handover_memo || ''}
          updatedByName={d.handover_memo_updated_by_name}
          updatedAt={d.handover_memo_updated_at}
          history={data.handover_memo_history || []}
          canEdit={editable} onChanged={load} />
        <div className="mt-3">
          <CompletionCard devId={id} canEdit={editable} isAdmin={isAdmin} onChanged={load} />
        </div>
      </div>

      <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-stage-history">
        <div className="border-b border-zinc-300 bg-zinc-100 px-4 py-2">
          <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
            <Clock size={14} /> ステージ履歴
          </p>
        </div>
        {(data.stage_logs || []).length === 0 ? (
          <EmptyState title="履歴はまだありません" testid="dev-stage-history-empty" className="m-4" />
        ) : (
          <ul className="divide-y divide-zinc-200">
            {data.stage_logs.map((l) => (
              <li key={l.id} className="px-4 py-2.5 text-sm" data-testid={`dev-stage-log-${l.id}`}>
                {l.action === 'deleted' || l.action === 'restored' ? (
                  <StatusBadge tone={l.action === 'deleted' ? 'danger' : 'info'}
                    testid={`dev-stage-log-action-${l.id}`}>
                    {l.action === 'deleted' ? '削除' : '削除を取り消し'}
                  </StatusBadge>
                ) : (
                  <span className="font-semibold text-zinc-900">
                    {l.from_stage_label ? `${l.from_stage_label} → ` : ''}{l.to_stage_label}
                  </span>
                )}
                {l.forced && (
                  <StatusBadge tone="warning" className="ml-2" testid={`dev-stage-log-forced-${l.id}`}>
                    例外進行
                  </StatusBadge>
                )}
                <span className="ml-2 text-xs text-zinc-600">
                  {l.performed_by_name} · {jstDateTime(l.performed_at)}
                </span>
                {l.reason && <p className="text-xs text-zinc-700">理由: {l.reason}</p>}
                {l.unmet_gates?.length > 0 && (
                  <p className="text-xs text-amber-800">未達条件: {l.unmet_gates.join('、')}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <NotebookHeading no="6" title="この案件からの学び"
        hint="過去の実績から、次に気をつけることです。" />
      <div id="dev-learning">
        <LearningCard devId={id} developmentType={d.development_type} context="detail"
          canEdit={editable} />
      </div>

      <div id="dev-ai">
        <AiWorkspace devId={id} canEdit={editable} concept={d.concept || {}} onApplied={load}
          linkedDocs={[
            ...data.checklist.flatMap((c) => c.documents || []),
            ...(data.unassigned_documents || []),
          ]} />
      </div>

      <TraceabilityPanel entityType="development_project" entityId={id} />

      <DevDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} dev={d}
        onDeleted={load} />

      <DevRollbackDialog open={rollbackOpen} onOpenChange={setRollbackOpen} devId={id}
        onDone={load} />

      <Dialog open={!!gateOpen} onOpenChange={(o) => !o && setGateOpen(null)}>
        <DialogContent className="bg-white" data-testid="dev-gate-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">次のステップへ進むための条件が満たされていません。</DialogTitle>
            <DialogDescription className="text-sm">
              以下を満たしてから、もう一度お試しください。
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-900" data-testid="dev-gate-unmet">
            {(gateOpen?.unmet || []).map((u, i) => (
              <li key={i} data-testid={`dev-gate-unmet-${i}`}>
                {u.label}{u.detail ? `（${u.detail}）` : ''}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGateOpen(null)} data-testid="dev-gate-close-btn">閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={forceOpen} onOpenChange={setForceOpen}>
        <DialogContent className="bg-white" data-testid="dev-force-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">高度な操作：ステージの例外進行</DialogTitle>
            <DialogDescription className="text-sm">
              条件を満たしていなくてもステージを進めます。理由は必須で、履歴に記録されます。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">進める先のステージ</Label>
              <Select value={forceTo} onValueChange={setForceTo}>
                <SelectTrigger data-testid="dev-force-stage-select">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {data.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">理由（必須）</Label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="進行理由を入力してください（必須）" data-testid="dev-force-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceOpen(false)}>閉じる</Button>
            <Button onClick={force} disabled={!forceTo || reason.trim().length < 3}
              data-testid="dev-force-submit-btn">例外として進む</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
